import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useLocationStore } from '@/store/location';
import { subscribeToInventoryCounts, unsubscribe } from '@/lib/realtime';
import { toast } from 'sonner';
import type { InventoryCount, InventoryCountItem } from './types';

// ──────────────────────────────────────────────
// Query keys
// ──────────────────────────────────────────────
export const countKeys = {
  all: ['inventory-counts'] as const,
  lists: () => [...countKeys.all, 'list'] as const,
  list: (orgId: string) => [...countKeys.lists(), orgId] as const,
  details: () => [...countKeys.all, 'detail'] as const,
  detail: (id: string) => [...countKeys.details(), id] as const,
  items: (countId: string) => [...countKeys.all, 'items', countId] as const,
};

// ──────────────────────────────────────────────
// Real-time hook
// ──────────────────────────────────────────────
export function useInventoryCountsRealtime() {
  const { currentLocation } = useLocationStore();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!currentLocation?.id) return;
    const channel = subscribeToInventoryCounts(currentLocation.id, () => {
      queryClient.invalidateQueries({ queryKey: countKeys.all });
    });
    return () => {
      unsubscribe(channel);
    };
  }, [currentLocation?.id, queryClient]);
}

// ──────────────────────────────────────────────
// List query
// ──────────────────────────────────────────────
export function useInventoryCountsList() {
  const { organization } = useAuth();

  return useQuery({
    queryKey: countKeys.list(organization?.id ?? ''),
    queryFn: async () => {
      if (!organization?.id) return [];
      const { data, error } = await supabase
        .from('inventory_counts')
        .select(`
          *,
          location:locations(id, name),
          purchase_order:purchase_orders(id, order_number, status),
          goods_request:goods_transfers!goods_request_id(id, transfer_number, status)
        `)
        .eq('organization_id', organization.id)
        .order('scheduled_at', { ascending: false });
      if (error) throw error;
      return data as InventoryCount[];
    },
    enabled: !!organization?.id,
  });
}

// ──────────────────────────────────────────────
// Single count detail query
// ──────────────────────────────────────────────
export function useInventoryCount(countId: string | undefined) {
  return useQuery({
    queryKey: countKeys.detail(countId ?? ''),
    queryFn: async () => {
      if (!countId) return null;
      const { data, error } = await supabase
        .from('inventory_counts')
        .select(`
          *,
          location:locations(id, name),
          purchase_order:purchase_orders(id, order_number, status),
          goods_request:goods_transfers!goods_request_id(id, transfer_number, status)
        `)
        .eq('id', countId)
        .single();
      if (error) throw error;
      return data as InventoryCount;
    },
    enabled: !!countId,
  });
}

// ──────────────────────────────────────────────
// Count items query
// ──────────────────────────────────────────────
export function useInventoryCountItems(countId: string | undefined) {
  return useQuery({
    queryKey: countKeys.items(countId ?? ''),
    queryFn: async () => {
      if (!countId) return [];
      const { data, error } = await supabase
        .from('inventory_count_items')
        .select(`
          *,
          product:products(id, name, sku, image_url, barcode)
        `)
        .eq('count_id', countId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as InventoryCountItem[];
    },
    enabled: !!countId,
  });
}

// ──────────────────────────────────────────────
// Mutations
// ──────────────────────────────────────────────

export function useCreateCount() {
  const { organization, user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: {
      location_id: string;
      name: string;
      count_type: 'full' | 'partial';
      include_inactive_products: boolean;
      scheduled_date: string;
      scheduled_time: string;
      notes: string;
      product_ids: string[];
      category_ids: string[];
      linked_entity_type: 'purchase_order' | 'goods_request' | null;
      purchase_order_id: string;
      goods_request_id: string;
      linked_entity_notes: string;
    }) => {
      const countNumber = `CNT-${Date.now().toString(36).toUpperCase()}`;
      const scheduledAt = new Date(`${formData.scheduled_date}T${formData.scheduled_time}`);

      const filters: Record<string, string[]> = {};
      if (formData.count_type === 'partial') {
        if (formData.product_ids.length > 0) filters.product_ids = formData.product_ids;
        if (formData.category_ids.length > 0) filters.category_ids = formData.category_ids;
      }

      const { data: count, error: countError } = await supabase
        .from('inventory_counts')
        .insert({
          organization_id: organization?.id,
          location_id: formData.location_id,
          count_number: countNumber,
          name: formData.name || null,
          count_type: formData.count_type,
          include_inactive_products: formData.include_inactive_products,
          filters,
          scheduled_at: scheduledAt.toISOString(),
          status: 'scheduled',
          created_by: user?.id,
          notes: formData.notes || null,
          linked_entity_type: formData.linked_entity_type,
          purchase_order_id:
            formData.linked_entity_type === 'purchase_order'
              ? formData.purchase_order_id || null
              : null,
          goods_request_id:
            formData.linked_entity_type === 'goods_request'
              ? formData.goods_request_id || null
              : null,
          linked_entity_notes: formData.linked_entity_notes || null,
        })
        .select()
        .single();

      if (countError) throw countError;

      // Generate count items
      const { error: genError } = await supabase.rpc('generate_inventory_count_items', {
        p_count_id: count.id,
      });
      if (genError) {
        console.error('Error generating count items:', genError);
      }

      return count;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: countKeys.all });
      toast.success('Inventory count created');
    },
    onError: (error: Error) => {
      toast.error('Failed to create count', { description: error.message });
    },
  });
}

export function useStartCount() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (countId: string) => {
      const { error } = await supabase
        .from('inventory_counts')
        .update({
          status: 'in_progress',
          started_at: new Date().toISOString(),
          started_by: user?.id,
        })
        .eq('id', countId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: countKeys.all });
      toast.success('Count started');
    },
  });
}

export function useUpdateCountItem() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      itemId,
      countedQty,
      reason,
    }: {
      itemId: string;
      countedQty: number;
      reason?: string;
      countId?: string;
    }) => {
      const { error } = await supabase
        .from('inventory_count_items')
        .update({
          counted_quantity: countedQty,
          variance_reason: reason || null,
          counted_by: user?.id,
          counted_at: new Date().toISOString(),
        })
        .eq('id', itemId);
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      if (variables.countId) {
        queryClient.invalidateQueries({ queryKey: countKeys.items(variables.countId) });
      }
    },
  });
}

export function useCompleteCount() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      countId,
      items,
      countQuantities,
      varianceReasons,
    }: {
      countId: string;
      items: InventoryCountItem[];
      countQuantities: Record<string, string>;
      varianceReasons: Record<string, string>;
    }) => {
      // Ensure the count is in_progress before attempting to complete
      const { data: currentCount } = await supabase
        .from('inventory_counts')
        .select('status')
        .eq('id', countId)
        .single();

      if (currentCount?.status === 'scheduled') {
        // Auto-start the count first
        const { error: startError } = await supabase
          .from('inventory_counts')
          .update({
            status: 'in_progress',
            started_at: new Date().toISOString(),
            started_by: user?.id,
          })
          .eq('id', countId);
        if (startError) throw startError;
      }

      // Save all counted quantities first
      for (const item of items) {
        const countedQty = countQuantities[item.id];
        if (countedQty !== undefined && countedQty !== '') {
          const { error } = await supabase
            .from('inventory_count_items')
            .update({
              counted_quantity: parseFloat(countedQty),
              variance_reason: varianceReasons[item.id] || null,
              counted_by: user?.id,
              counted_at: new Date().toISOString(),
            })
            .eq('id', item.id);
          if (error) throw error;
        }
      }

      // Then complete the count
      const { error } = await supabase.rpc('complete_inventory_count', {
        p_count_id: countId,
        p_user_id: user?.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: countKeys.all });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-items'] });
      queryClient.invalidateQueries({ queryKey: ['raw-inventory-stock'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] });
      toast.success('Count completed', { description: 'Inventory has been updated' });
    },
    onError: (error: Error) => {
      toast.error('Failed to complete count', {
        description: error.message || 'An unexpected error occurred',
      });
    },
  });
}

export function useCancelCount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (countId: string) => {
      const { error } = await supabase
        .from('inventory_counts')
        .update({ status: 'canceled' })
        .eq('id', countId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: countKeys.all });
      toast.success('Count canceled');
    },
  });
}

export function useDeleteCount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (countId: string) => {
      const { error } = await supabase
        .from('inventory_counts')
        .delete()
        .eq('id', countId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: countKeys.all });
      toast.success('Count deleted');
    },
  });
}

export function useAddCountItem() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      countId,
      productId,
      countedQuantity,
      expectedQuantity,
      inventoryItemId,
      unitCost,
    }: {
      countId: string;
      productId: string;
      countedQuantity: number;
      expectedQuantity?: number;
      inventoryItemId?: string;
      unitCost?: number;
    }) => {
      const { data, error } = await supabase
        .from('inventory_count_items')
        .insert({
          count_id: countId,
          product_id: productId,
          expected_quantity: expectedQuantity ?? 0,
          counted_quantity: countedQuantity,
          inventory_item_id: inventoryItemId || null,
          unit_cost: unitCost ?? 0,
          counted_by: user?.id,
          counted_at: new Date().toISOString(),
        })
        .select(`
          *,
          product:products(id, name, sku, image_url, barcode)
        `)
        .single();
      if (error) throw error;
      return data as InventoryCountItem;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: countKeys.items(variables.countId) });
    },
  });
}

// ──────────────────────────────────────────────
// Purchase orders & goods transfers for linking
// ──────────────────────────────────────────────

export function usePurchaseOrdersForLinking(
  locationId: string,
  enabled: boolean
) {
  const { organization } = useAuth();

  return useQuery({
    queryKey: ['purchase-orders-for-linking', organization?.id, locationId],
    queryFn: async () => {
      if (!organization?.id) return [];
      let query = supabase
        .from('purchase_orders')
        .select('id, order_number, status, supplier:suppliers(name), created_at')
        .eq('organization_id', organization.id)
        .in('status', ['ordered', 'partial', 'received'])
        .order('created_at', { ascending: false })
        .limit(50);

      if (locationId) {
        query = query.contains('location_ids', [locationId]);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data as unknown) as Array<{
        id: string;
        order_number: string;
        status: string;
        supplier: { name: string } | null;
        created_at: string;
      }>;
    },
    enabled: !!organization?.id && enabled,
  });
}

export function useGoodsTransfersForLinking(
  locationId: string,
  enabled: boolean
) {
  const { organization } = useAuth();

  return useQuery({
    queryKey: ['goods-transfers-for-linking', organization?.id, locationId],
    queryFn: async () => {
      if (!organization?.id) return [];
      let query = supabase
        .from('goods_transfers')
        .select(
          'id, transfer_number, status, from_location:locations!goods_transfers_from_location_id_fkey(name), to_location:locations!goods_transfers_to_location_id_fkey(name), created_at'
        )
        .eq('organization_id', organization.id)
        .in('status', ['pending', 'in_transit', 'partially_received', 'received'])
        .order('created_at', { ascending: false })
        .limit(50);

      if (locationId) {
        query = query.or(
          `from_location_id.eq.${locationId},to_location_id.eq.${locationId}`
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data as unknown) as Array<{
        id: string;
        transfer_number: string;
        status: string;
        from_location: { name: string } | null;
        to_location: { name: string } | null;
        created_at: string;
      }>;
    },
    enabled: !!organization?.id && enabled,
  });
}
